import { Trophy, ShieldCheck, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { ConfidenceChip } from "@/features/simulations/ConfidenceChip";
import { cn } from "@/lib/utils";
import type { AutoBatchResponse, AutoCandidate } from "@opus-dt/shared-types";
import { objMetric, objDelta, fmtMetric, objLabel } from "./metrics";
import { HeroComparison } from "./charts/HeroComparison";

interface RecommendationHeroProps {
  batch: AutoBatchResponse;
  winner: AutoCandidate | null;
  baseline: AutoCandidate | null;
}

// Hero da recomendação (métrica do objetivo + Δ vs base + gargalo +
// confiança). Estados "falhou", "sem ganho" (NO_GAIN) e "parcial" são telas de 1ª classe.
export function RecommendationHero({ batch, winner, baseline }: RecommendationHeroProps) {
  // FAILED/CANCELLED-sem-vencedor: nunca disfarçar de "sem ganho" — mostrar o erro honestamente.
  if (batch.status === "FAILED" || (batch.status === "CANCELLED" && !winner)) {
    return (
      <Card className="mb-4 border-destructive/40">
        <CardContent className="space-y-2 pt-6">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-destructive">
            <AlertTriangle className="h-3.5 w-3.5" />
            {batch.status === "FAILED" ? "A busca falhou" : "Busca cancelada"}
          </span>
          <h2 className="text-lg font-semibold">Nenhuma recomendação disponível</h2>
          <p className="text-sm text-muted-foreground">
            {batch.errorMessage ??
              "A busca foi interrompida antes de avaliar qualquer candidato. Ajuste a configuração e rode de novo."}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (batch.status === "NO_GAIN" || !winner) {
    return (
      <Card className="mb-4 border-border bg-muted/30">
        <CardContent className="space-y-2 pt-6">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-2 py-0.5 text-xs font-medium text-background">
            <ShieldCheck className="h-3.5 w-3.5" />
            Sem ganho encontrado
          </span>
          <h2 className="text-lg font-semibold">A configuração atual já é a melhor encontrada</h2>
          <p className="text-sm text-muted-foreground">
            Nenhum dos {batch.candidateCount} candidatos superou a linha base no objetivo escolhido,
            dentro de ±{batch.variationPct}%. Aumentar a faixa ou rever o objetivo pode abrir
            espaço. Promover está indisponível.
          </p>
        </CardContent>
      </Card>
    );
  }

  const metric = objMetric(winner, batch);
  const delta = objDelta(winner, baseline, batch);
  const baseMetric = baseline && baseline !== winner ? objMetric(baseline, batch) : null;

  return (
    <Card className="mb-4">
      <CardContent className="space-y-3 pt-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
              <Trophy className="h-3.5 w-3.5" />
              Configuração recomendada
            </span>
            <h2 className="mt-1 text-lg font-semibold">{winner.label ?? "Candidato vencedor"}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              {winner.bottleneckNames.length > 0 ? (
                <span>
                  Gargalo: <b className="text-foreground">{winner.bottleneckNames.join(", ")}</b>
                </span>
              ) : null}
              <ConfidenceChip
                level={winner.displayedConfidenceLevel ?? winner.confidenceLevel}
                basis={winner.confidenceBasis}
                trackRecordSource={winner.trackRecordSource}
              />
            </div>
          </div>
          {batch.partial ? (
            <span className="rounded-full border border-amber-500/40 px-2 py-0.5 text-xs font-medium text-amber-600">
              Recomendação parcial · {batch.evaluatedCount} de {batch.candidateCount + 1} avaliados
            </span>
          ) : null}
        </div>

        <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
          <div>
            <p className="text-xs text-muted-foreground">{objLabel(batch.objective)}</p>
            <p className="font-mono text-3xl font-semibold tabular-nums">{fmtMetric(metric)}</p>
            {delta ? (
              <span
                className={cn(
                  "text-sm font-medium",
                  delta.positive ? "text-emerald-600" : "text-destructive"
                )}
              >
                {delta.text} vs base
              </span>
            ) : null}
          </div>
          {/* O tamanho do ganho deixa de ser uma subtração de cabeça. */}
          <HeroComparison base={baseMetric} winner={metric} delta={delta} />
        </div>
      </CardContent>
    </Card>
  );
}
